import { useState } from "react";
import { Mail, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Compact modal that lets the recruiter send the current candidate brief
 * to a hiring manager via email. Backend mints a one-time tokenised link.
 */
export default function SendBriefModal({ interviewId, candidateName }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/send-brief`, {
        to_email: email.trim(),
        to_name: name.trim(),
        note: note.trim(),
      });
      toast.success(`Brief on ${candidateName} sent to ${email}`);
      setOpen(false);
      setEmail("");
      setName("");
      setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send brief");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        data-testid="open-send-brief-modal-btn"
        onClick={() => setOpen(true)}
        className="rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
      >
        <Mail className="mr-2 h-3 w-3" />
        Send to hiring manager
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="send-brief-modal"
          className="max-w-md rounded-sm"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Send className="h-5 w-5 text-emerald-600" />
              Send brief to hiring manager
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              They&apos;ll get a one-time link to view scores, rubric, transcripts +
              record a 👍 / 👎 / maybe with a comment. Response routes back to
              your inbox.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Hiring manager email
              </Label>
              <Input
                data-testid="send-brief-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@yourcompany.com"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Name (optional)
              </Label>
              <Input
                data-testid="send-brief-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                Cover note (optional)
              </Label>
              <textarea
                data-testid="send-brief-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={400}
                placeholder="Quick read - calibration only…"
                className="mt-1.5 w-full rounded-sm border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={busy}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                data-testid="send-brief-submit-btn"
                disabled={!email.trim() || busy}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-500"
              >
                {busy ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3 w-3" />
                )}
                Send
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
